import React, { useEffect } from 'react';
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { BarChart } from 'lucide-react';

// Mapa de días en inglés a español
const weekdayMap: Record<string, string> = {
  'Mon': 'Lun',
  'Tue': 'Mar',
  'Wed': 'Mié',
  'Thu': 'Jue',
  'Fri': 'Vie',
  'Sat': 'Sáb',
  'Sun': 'Dom'
};

// Orden base de los días para posicionamiento consistente
const weekdayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Reordena la semana para iniciar el gráfico desde el día siguiente al actual
function reorderDays(data: any[]) {
  const today = new Date();
  const weekday = today.toLocaleDateString('en-US', { weekday: 'short' });
  const todayIndex = weekdayOrder.indexOf(weekday);
  return [...data.slice(todayIndex + 1), ...data.slice(0, todayIndex + 1)];
}

interface WeeklyApiComparisonChartProps {
  semana_actual?: Record<string, number>;    // Datos actuales por día (e.g. {"Mon": 20, ...})
  semana_anterior?: Record<string, number>;  // Datos de la semana previa por día
}

function WeeklyApiComparisonChart({ 
  semana_actual = {}, 
  semana_anterior = {}
}: WeeklyApiComparisonChartProps) {
  // Días únicos combinando ambas semanas
  const uniqueDays = Array.from(new Set([
    ...Object.keys(semana_actual || {}),
    ...Object.keys(semana_anterior || {}),
  ])).sort((a, b) => weekdayOrder.indexOf(a) - weekdayOrder.indexOf(b));

  // Fusiona los datos en un arreglo con claves en español
  const fullData = uniqueDays.map((key) => ({
    dia: key,
    dia_es: weekdayMap[key] || key, 
    actual: semana_actual[key] || 0,
    anterior: semana_anterior[key] || 0,
  }));

  // Reordena y filtra para mostrar solo días con datos
  const orderedData = reorderDays(fullData);
  const filteredData = orderedData.filter(d => d.actual > 0 || d.anterior > 0);

  // Depuración: muestra resumen de datos en consola
  useEffect(() => {
    console.group("📊 Análisis de comparación semanal");
    console.log("📆 Claves semana actual:", Object.keys(semana_actual));
    console.log("📆 Claves semana anterior:", Object.keys(semana_anterior));
    console.log("🗂 Días únicos:", uniqueDays);
    console.log("📊 Datos finales:", fullData);
    console.groupEnd();
  }, [semana_actual, semana_anterior]);

  // Tooltip personalizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const diaEspanol = weekdayMap[label] || label;
      
      return (
        <div className="bg-[color:var(--acc2)]/20 backdrop-blur-sm p-3 rounded-lg border border-[color:var(--acc1)]/25 shadow-lg">
          <p className="text-white font-medium">{diaEspanol}</p>
          <div className="space-y-1 mt-1">
            {payload.map((entry: any, index: number) => (
              <div 
                key={index} 
                className="flex justify-between gap-4 text-sm"
              >
                <span style={{ color: index === 1 ? '#A78BFA' : '#93C5FD' }}>
                  {entry.name}:
                </span>
                <span className="font-medium text-white">
                  {entry.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] backdrop-blur-sm border border-[color:var(--acc1)]/20 rounded-xl p-6 shadow-lg transition-all duration-300 hover:shadow-xl">
      <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
        <BarChart className="mr-2 text-[color:var(--acc1)]/90" size={20} />
        Comparativa Día a Día (2 semanas)
      </h2>

      {/* Si no hay datos, mostrar mensaje */}
      {filteredData.length === 0 ? (
        <div className="text-center text-[color:var(--acc1)]/90 bg-[color:var(--acc2)]/10 border border-[color:var(--acc1)]/20 rounded-lg py-10">
          <p className="text-sm">No hay datos suficientes para mostrar la comparación semanal.</p>
        </div>
      ) : (
        // Gráfico principal de barras comparativas
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ReBarChart
              data={filteredData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              barGap={0}
            >
              {/* Gradientes de color para cada barra */}
              <defs>
                <linearGradient id="barAnterior" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.9} />
                  <stop offset="95%" stopColor="#1E40AF" stopOpacity={0.2} />
                </linearGradient>
                <linearGradient id="barActual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#A855F7" stopOpacity={0.9} />
                  <stop offset="95%" stopColor="#6D28D9" stopOpacity={0.2} />
                </linearGradient>
              </defs>
            
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#4f46e5" 
                opacity={0.15} 
                vertical={false}
              />
              
              <XAxis 
                dataKey="dia_es" 
                stroke="#c7d2fe" 
                fontSize={12}
                tickLine={false}
                axisLine={{ stroke: '#4f46e530' }}
              />
              
              <YAxis 
                stroke="#c7d2fe" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: '#4338ca20' }}
              />
              
              <Bar 
                name="Semana Anterior" 
                dataKey="anterior" 
                fill="url(#barAnterior)" 
                radius={[4, 4, 0, 0]} 
                maxBarSize={60}  
              />
              
              <Bar 
                name="Semana Actual" 
                dataKey="actual" 
                fill="url(#barActual)" 
                radius={[4, 4, 0, 0]}
                maxBarSize={60}  
              />
            </ReBarChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {/* Leyenda inferior para colores de referencia */}
      <div className="flex justify-center mt-4 space-x-6 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-600 rounded-sm mr-2"></div>
          <span className="text-[color:var(--acc1)]/85">Semana Anterior</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-[color:var(--acc2)] rounded-sm mr-2"></div>
          <span className="text-[color:var(--acc1)]/85">Semana Actual</span>
        </div>
      </div>
    </div>
  );
}

export { WeeklyApiComparisonChart };
